import { action, makeAutoObservable, runInAction } from 'mobx';
import { logger } from '@cutwise/utils';

import useStore from '@/common/hooks/useStore';
import { HttpStatus } from '@/common/network/constants/httpStatus';
import Notifier from '@/common/services/Notifier';
import Storage, { KEY_SERVICES_PENDING_SERVICE } from '@/common/services/Storage';
import { ServiceOrderStatus } from '@/conversion/constants/serviceOrderStatus';
import { ServiceType } from '@/conversion/constants/serviceType';
import { ServiceOrder } from '@/conversion/entities/ServiceOrder';
import { GetFreeTierResponse } from '@/conversion/types/GetFreeTierResponse';
import { AuthModalConfirmation } from '@/user/components/AuthModal/AuthModal';
import UserStore, { userStore } from '@/user/stores/UserStore';

import ServicesAPI, { CreateOrderRequest } from '../api/ServicesAPI';

const FETCH_NEW_ORDERS_LIST_INTERVAL = 10000;
const MINUTE_DURATION = 60000;
const MINUTES_IN_HOUR = 60;

interface ServicesStoreFilter {
  filename: string;
  services: ServiceType[];
}

const DEFAULT_FILTER: ServicesStoreFilter = { filename: '', services: [] };

export default class ServicesStore {
  isFetchingOrders = false;

  isCreatingOrder = false;

  orderList: ServiceOrder[] = [];

  filter = DEFAULT_FILTER;

  freeTier: Nullable<GetFreeTierResponse> = null;

  insufficientBalanceMessage: Nullable<string> = null;

  currentTime = Date.now();

  readonly conversionAPI = new ServicesAPI();

  private requestOrderInterval?: SetInterval;

  private clockTimeout?: SetTimeout;

  constructor(private readonly userStore: UserStore) {
    makeAutoObservable(this);
  }

  get isOrderListEmpty() {
    return this.orderList.length === 0;
  }

  get isFilterActive() {
    return this.activeFiltersCount > 0;
  }

  get activeFiltersCount() {
    return Number(this.filter.filename !== '') + this.filter.services.length;
  }

  get filteredOrderList() {
    const { filename, services } = this.filter;

    return this.orderList.filter(
      (order) => order.name.toLowerCase().includes(filename.toLowerCase()) &&
        (services.length === 0 || services.includes(order.service)),
    );
  }

  get isFreeAvailable() {
    return Boolean(this.freeTier?.isAvailable) || this.freeWait?.count === 0;
  }

  get freeWait() {
    if (!this.freeTier?.availableFrom) {
      return null;
    }

    const waitDuration = Math.max(0, new Date(this.freeTier.availableFrom).getTime() - this.currentTime);
    const minutesCount = Math.ceil(waitDuration / MINUTE_DURATION);

    return minutesCount < MINUTES_IN_HOUR
      ? { count: minutesCount, unit: 'minute' }
      : { count: Math.ceil(minutesCount / MINUTES_IN_HOUR), unit: 'hour' };
  }

  initStore = () => {
    Storage.removeObject(KEY_SERVICES_PENDING_SERVICE, Storage.STORAGE_SESSION);
    this.fetchOrderList();
    this.fetchFreeTier();
    this.tickClock();
  };

  fetchOrderList = async () => {
    this.isFetchingOrders = true;

    try {
      await this.getOrdersListRequest();

      clearInterval(this.requestOrderInterval);
      runInAction(() => {
        this.requestOrderInterval = setInterval(() => this.refetchOrdersListRequest(), FETCH_NEW_ORDERS_LIST_INTERVAL);
      });
    } catch (e) {
      Notifier.error(e);
    }

    runInAction(() => {
      this.isFetchingOrders = false;
    });
  };

  createOrder = (data: CreateOrderRequest) => {
    this.isCreatingOrder = true;

    return this.conversionAPI
      .createOrder(data)
      .then((response) => {
        this.getOrdersListRequest();
        this.fetchFreeTier();

        return response.content;
      })
      .finally(
        action(() => {
          this.isCreatingOrder = false;
        }),
      );
  };

  prepareCreateOrder = (service: ServiceType, title: string) => {
    if (!this.userStore.isAuthorized) {
      Storage.saveObject(KEY_SERVICES_PENDING_SERVICE, service, Storage.STORAGE_SESSION);

      AuthModalConfirmation({
        title,
        description:
          'Your orders are saved to your account, so you can always get back to your results — on any device, at any time.',
        onAfterClose: () => Storage.removeObject(KEY_SERVICES_PENDING_SERVICE, Storage.STORAGE_SESSION),
      });

      return false;
    }

    this.insufficientBalanceMessage = null;
    this.fetchFreeTier();

    return true;
  };

  uploadOrder = async (service: ServiceType, files: File[]) => {
    if (!files[0]) {
      return false;
    }

    this.insufficientBalanceMessage = null;

    try {
      await this.createOrder({ service, file1: files[0] });

      Notifier.success('Processing started! You will receive email with details about next steps.');

      return true;
    } catch (e) {
      if (e.code === HttpStatus.PaymentRequired) {
        runInAction(() => {
          this.insufficientBalanceMessage = e.message;
        });
      } else {
        Notifier.error(e.message);
      }

      return false;
    }
  };

  setServicesFilter = (filter: Partial<ServicesStoreFilter>) => {
    this.filter = { ...this.filter, ...filter };
  };

  toggleServiceFilter = (service: ServiceType) => {
    const { services } = this.filter;

    this.setServicesFilter({
      services: services.includes(service) ? services.filter((item) => item !== service) : [...services, service],
    });
  };

  resetServicesFilter = () => {
    this.filter = DEFAULT_FILTER;
  };

  dispose = () => {
    clearInterval(this.requestOrderInterval);
    clearTimeout(this.clockTimeout);
  };

  private fetchFreeTier = async () => {
    try {
      const response = await this.conversionAPI.getFreeTierState();

      runInAction(() => {
        this.freeTier = response?.content ?? null;
        this.currentTime = Date.now();
      });
    } catch (e) {
      runInAction(() => {
        this.freeTier = null;
      });
      logger.error(e);
    }
  };

  private tickClock = () => {
    clearTimeout(this.clockTimeout);
    this.clockTimeout = setTimeout(
      action(() => {
        this.currentTime = Date.now();
        this.tickClock();
      }),
      MINUTE_DURATION,
    );
  };

  private readonly getOrdersListRequest = () => this.conversionAPI.getOrderList().then(
    action((res) => {
      if (!res?.content) {
        return;
      }

      this.orderList = res.content.map((order) => new ServiceOrder(order));
    }),
  );

  private readonly refetchOrdersListRequest = async () => {
    // Check if at least one order has "Processing" status, if yes - re-fetch list, if not - do nothing
    if (this.orderList?.some((order) => order.status === ServiceOrderStatus.Processing)) {
      await this.getOrdersListRequest();
    }
  };
}

export const useServicesStore = () => useStore(ServicesStore);

export const servicesStore = new ServicesStore(userStore);
